/**
 * @type {Object}
 * @description The memoize store module state
 */
const state = {
  cache: {},
}

/**
 * @type {Object}
 * @description The memoize store module mutations
 * @property {Function} cache
 * @property {Function} clear
 * @property {Function} clearAll
 * @description The memoize store module mutations
 */
const mutations = {
  /**
   * @param {Object} state
   * @param {Object} payload
   * @param {String} payload.key
   * @param {Object} payload.value
   * @description Sets the cache key to the value
   * @example
   * store.commit('memoize/cache', {
   *  key: 'myKey',
   *  value: { foo: 'bar' },
   * })
   * // state.cache.myKey = { foo: 'bar' }
   * @returns {undefined}
   */
  cache(state, { key, value }) {
    state.cache[key] = value
  },

  /**
   * @param {Object} state
   * @param {String} key
   * @description Clears the cache key
   * @example
   * store.commit('memoize/clear', 'myKey')
   * // state.cache.myKey = undefined
   * @returns {undefined}
   */
  clear(state, key) {
    state.cache[key] = undefined
  },

  /**
   * @param {Object} state
   * @description Clears the entire cache
   * @example
   * store.commit('memoize/clearAll')
   * // state.cache = {}
   * @returns {undefined}
   */
  clearAll(state) {
    state.cache = {}
  },
}

const actions = {
  /**
   * @param {Object} context
   * @param {Object} payload
   * @param {String} payload.key
   * @param {Object} payload.value
   * @description Sets the cache key to the value
   * @example
   * store.dispatch('memoize/cache', {
   * key: 'myKey',
   * value: { foo: 'bar' },
   * })
   * // state.cache.myKey = { foo: 'bar' }
   * @returns {undefined}
   */
  cache({ commit }, { key, value }) {
    commit('cache', { key, value })
  },

  /**
   * @param {Object} context
   * @param {String} key
   * @description Clears the cache key
   * @example
   */
  clear({ commit }, key) {
    commit('clear', key)
  },

  /**
   * @param {Object} context
   * @description Clears the entire cache
   * @example
   * store.dispatch('memoize/clearAll')
   * // state.cache = {}
   * @returns {undefined}
   */
  clearAll({ commit }) {
    commit('clearAll')
  },
}

/**
 * @type {Object}
 * @property {Function} cache
 * @description The memoize store module getters
 * @example
 */
const getters = {
  /**
   * @param {Object} state
   * @returns {Object}
   * @description Returns the cache object
   * @example
   * store.getters['memoize/cache']
   * // { myKey: { foo: 'bar' } }
   * @returns {Object}
   */
  cache: (state) => state.cache,
}

export default {
  namespaced: true,
  mutations,
  getters,
  actions,
  state,
}
